import { useEffect, useRef, useState } from "react";

import ModalWrapper from "src/components/common/modal/ModalWrapper";

/**
 * Base64 이미지를 모달에서 확대해 확인하는 공통 컴포넌트입니다.
 * 이미지 데이터는 변경하지 않고 화면 배율과 이동 위치만 내부 상태로 관리합니다.
 */
interface ImagePreviewModalProps {
	/** 팝업 표시 여부 */
	isOpen: boolean;
	/** 팝업 헤더와 이미지 대체 텍스트에 사용할 제목 */
	title: string;
	/** data URL 접두사를 제외한 Base64 이미지 문자열 */
	image: string;
	/** 팝업 닫기 처리 */
	onClose: () => void;
}

/** 확대 이미지의 중앙 기준 이동 거리(px) */
interface ImagePosition {
	x: number;
	y: number;
}

// 배율은 25%~400% 범위에서 25% 단위로 변경합니다.
const MIN_IMAGE_SCALE = 0.25;
const MAX_IMAGE_SCALE = 4;
const IMAGE_SCALE_STEP = 0.25;
// 방향키를 한 번 입력했을 때 이동할 거리입니다.
const IMAGE_MOVE_STEP = 40;

export const ImagePreviewModal = ({
	isOpen,
	title,
	image,
	onClose,
}: ImagePreviewModalProps) => {
	// 이동 가능 범위를 계산하기 위해 표시 영역과 이미지 DOM을 참조합니다.
	const viewportRef = useRef<HTMLDivElement>(null);
	const imageRef = useRef<HTMLImageElement>(null);
	// 드래그 시작 시점의 포인터 좌표와 이미지 위치를 렌더링 없이 보관합니다.
	const dragStartRef = useRef({
		pointerId: -1,
		clientX: 0,
		clientY: 0,
		positionX: 0,
		positionY: 0,
	});

	// 확대 배율과 이동 위치는 저장 데이터와 분리된 팝업 전용 상태입니다.
	const [scale, setScale] = useState(1);
	const [position, setPosition] = useState<ImagePosition>({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);

	/**
	 * 확대된 이미지가 표시 영역 밖으로 완전히 밀려나지 않도록
	 * 현재 배율에서 허용되는 최대 이동 범위로 좌표를 제한합니다.
	 */
	const getClampedPosition = (
		nextPosition: ImagePosition,
		nextScale: number,
	): ImagePosition => {
		const viewport = viewportRef.current;
		const previewImage = imageRef.current;

		if (!viewport || !previewImage || nextScale <= 1) {
			return { x: 0, y: 0 };
		}

		const maxX = Math.max(
			0,
			(previewImage.offsetWidth * nextScale - viewport.clientWidth) / 2,
		);
		const maxY = Math.max(
			0,
			(previewImage.offsetHeight * nextScale - viewport.clientHeight) / 2,
		);

		return {
			x: Math.min(maxX, Math.max(-maxX, nextPosition.x)),
			y: Math.min(maxY, Math.max(-maxY, nextPosition.y)),
		};
	};

	// 100% 배율과 중앙 위치로 되돌립니다. 팝업의 "맞춤" 버튼에서도 사용합니다.
	const resetImageView = () => {
		setScale(1);
		setPosition({ x: 0, y: 0 });
		setIsDragging(false);
	};

	// 허용 범위 안에서 배율을 변경하고 변경된 배율에 맞춰 위치를 다시 보정합니다.
	const handleScaleChange = (nextScale: number) => {
		const clampedScale = Math.min(
			MAX_IMAGE_SCALE,
			Math.max(MIN_IMAGE_SCALE, nextScale),
		);

		setScale(clampedScale);
		setPosition((currentPosition) =>
			getClampedPosition(currentPosition, clampedScale),
		);
	};

	// 100%보다 확대된 상태에서만 드래그 시작 좌표를 기록합니다.
	const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
		if (scale <= 1) return;

		event.currentTarget.setPointerCapture(event.pointerId);
		dragStartRef.current = {
			pointerId: event.pointerId,
			clientX: event.clientX,
			clientY: event.clientY,
			positionX: position.x,
			positionY: position.y,
		};
		setIsDragging(true);
	};

	// 포인터 이동량을 드래그 시작 위치에 더해 이미지 표시 위치를 갱신합니다.
	const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
		if (!isDragging || dragStartRef.current.pointerId !== event.pointerId) return;

		const nextPosition = {
			x: dragStartRef.current.positionX + event.clientX - dragStartRef.current.clientX,
			y: dragStartRef.current.positionY + event.clientY - dragStartRef.current.clientY,
		};

		setPosition(getClampedPosition(nextPosition, scale));
	};

	// 포인터 캡처를 해제하고 현재 드래그 동작을 종료합니다.
	const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
		if (dragStartRef.current.pointerId !== event.pointerId) return;

		if (event.currentTarget.hasPointerCapture(event.pointerId)) {
			event.currentTarget.releasePointerCapture(event.pointerId);
		}
		dragStartRef.current.pointerId = -1;
		setIsDragging(false);
	};

	// 포커스가 이미지 영역에 있을 때 방향키로 확대 이미지를 이동합니다.
	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (scale <= 1) return;

		const moveByKey: Record<string, ImagePosition> = {
			ArrowUp: { x: 0, y: IMAGE_MOVE_STEP },
			ArrowDown: { x: 0, y: -IMAGE_MOVE_STEP },
			ArrowLeft: { x: IMAGE_MOVE_STEP, y: 0 },
			ArrowRight: { x: -IMAGE_MOVE_STEP, y: 0 },
		};
		const move = moveByKey[event.key];

		if (!move) return;

		event.preventDefault();
		setPosition((currentPosition) =>
			getClampedPosition(
				{
					x: currentPosition.x + move.x,
					y: currentPosition.y + move.y,
				},
				scale,
			),
		);
	};

	// 팝업을 새로 열거나 이미지가 바뀌면 이전 배율과 이동 위치를 초기화합니다.
	useEffect(() => {
		if (isOpen) {
			resetImageView();
		}
	}, [isOpen, image]);

	if (!isOpen || !image) return null;

	return (
		// 공통 모달의 외부 클릭, ESC 닫기, 크기 조절 기능을 사용합니다.
		<ModalWrapper
			size="lg"
			width={960}
			overlay
			overlayOpacity={0.45}
			zIndex={60}
			onOverlayClick={onClose}
			onEscKeyDown={onClose}
		>
			<div className="w-full h-full flex flex-col overflow-hidden">
				{/* 팝업 제목과 상단 닫기 버튼 */}
				<div className="w-full h-[40px] shrink-0 bg-blue-dark text-white flex items-center justify-between px-[16px]">
					<span className="font-semibold truncate">{title}</span>
					<button
						type="button"
						className="w-[28px] h-[28px] flex items-center justify-center text-[22px]"
						onClick={onClose}
						aria-label="이미지 확대 팝업 닫기"
					>
						×
					</button>
				</div>

				{/*
					작은 원본도 팝업 영역에 맞춰 크게 표시합니다.
					object-contain으로 원본 비율을 유지하며 드래그와 방향키 입력을 처리합니다.
				*/}
				<div
					ref={viewportRef}
					className={`relative flex-1 min-h-0 overflow-hidden bg-gray-100 flex items-center justify-center outline-none ${
						scale > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-default"
					}`}
					tabIndex={0}
					onPointerDown={handlePointerDown}
					onPointerMove={handlePointerMove}
					onPointerUp={handlePointerUp}
					onPointerCancel={handlePointerUp}
					onKeyDown={handleKeyDown}
					aria-label="확대 이미지 영역"
				>
					<img
						ref={imageRef}
						className="w-full h-full object-contain pointer-events-none select-none"
						src={`data:image/png;base64,${image}`}
						alt={title}
						draggable={false}
						style={{
							transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${scale})`,
							transformOrigin: "center",
						}}
					/>
				</div>

				{/* 확대·축소, 현재 배율, 화면 맞춤, 닫기 조작 영역 */}
				<div className="w-full h-[44px] shrink-0 border-t border-gray-300 flex items-center justify-center gap-[6px]">
					<button
						type="button"
						className="h-[28px] min-w-[32px] rounded-[4px] bg-blue-dark text-white px-[8px] disabled:opacity-40"
						onClick={() => handleScaleChange(scale - IMAGE_SCALE_STEP)}
						disabled={scale <= MIN_IMAGE_SCALE}
						aria-label="이미지 축소"
					>
						−
					</button>
					<div className="h-[28px] min-w-[64px] rounded-[4px] bg-blue-dark text-white px-[8px] flex items-center justify-center">
						{Math.round(scale * 100)}%
					</div>
					<button
						type="button"
						className="h-[28px] min-w-[32px] rounded-[4px] bg-blue-dark text-white px-[8px] disabled:opacity-40"
						onClick={() => handleScaleChange(scale + IMAGE_SCALE_STEP)}
						disabled={scale >= MAX_IMAGE_SCALE}
						aria-label="이미지 확대"
					>
						+
					</button>
					<button
						type="button"
						className="h-[28px] rounded-[4px] bg-blue-dark text-white px-[10px]"
						onClick={resetImageView}
					>
						맞춤
					</button>
					<button
						type="button"
						className="h-[28px] rounded-[4px] bg-gray-400 text-white px-[10px]"
						onClick={onClose}
					>
						닫기
					</button>
				</div>
			</div>
		</ModalWrapper>
	);
};
